import { t } from '@if/core/i18n'
import { EWorksheetStatus, getEWorksheetStatusList } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { MerchantPayment } from '@src/data/types/MerchantPayment'
import { baseWorksheetSchema } from './BaseWorksheetSchema'
import { FinancingConfigDto } from './FinancingConfigDto'
import {
  IFinanceBaseWorksheet,
  IFinanceBaseWorksheetDecisionDTOSchema,
  IFinanceBaseWorksheetRevision,
} from './IFinanceBaseWorksheetSchema'

export const PersonalLoanWorksheetSchema = yup
  .object({
    amountRequested: yup.double().positive(),
    includeInsurance: yup.boolean().default(false),
    isHighEstimate: yup.boolean().default(false),
    highEstimateExplainedToClient: yup.boolean().default(false),
    addFastPayments: yup.boolean().default(false),
    fundConfirmationNote: yup.string().default('').nullable(),
    merchantPayments: yup.mixed<MerchantPayment[]>().default([]),
    paymentPlanId: yup.string().required(),
  })
  .concat(baseWorksheetSchema)

export type PersonalLoanWorksheet = IFinanceBaseWorksheet

export type PersonalWorksheetRevision = IFinanceBaseWorksheetRevision

export type PersonalWorksheetDecisionDTO = yup.InferType<typeof IFinanceBaseWorksheetDecisionDTOSchema>

export const buildEditPersonalLoanWorksheetDtoSchema = (financingConfig?: FinancingConfigDto) => {
  const editPersonalLoanWorksheetDtoSchema = yup.object({
    status: yup.mixed<EWorksheetStatus>().oneOf(getEWorksheetStatusList()).required().default(EWorksheetStatus.Draft),
    amountRequested: yup.double().positive(),
    deliveryOn: yup
      .string()
      .default('')
      .transform((value: string | null): string => (value === null ? '' : value))
      .required(),
    firstPaymentOn: yup
      .string()
      .dateOnly()
      .default(formatDate(new Date()))
      .nullable()
      .required()
      .typeError(() => t('mixed.required', { ns: 'validation' })),
    includeInsurance: yup.boolean().default(false),
    isHighEstimate: yup.boolean().default(false),
    highEstimateExplainedToClient: yup.boolean().default(false),
    addFastPayments: yup.boolean().default(false),
    paymentFrequency: yup.string().default('').required(),
    term: yup
      .integer()
      .min(6)
      .required()
      .when('amountRequested', {
        is: (value: number) => value && value < (financingConfig?.largeLoanAmount ?? 0),
        then: (schema) => {
          return schema.max(72, () =>
            t('common.errors.term84', {
              longTerm: financingConfig?.longTerm ?? 0,
              minAmountForLongTerm: financingConfig?.largeLoanAmount ?? 0,
            }),
          )
        },
        otherwise: (schema) => schema.max(financingConfig?.longTerm ?? 0),
      }),
    fundConfirmationNote: yup.string().default('').nullable(),
    merchantPayments: yup.mixed<MerchantPayment[]>().default([]),
    paymentPlanId: yup.string().required(),
    creditApplicationId: yup.string(),
    versionTag: yup.string(),
  })
  return editPersonalLoanWorksheetDtoSchema
}
export type EditPersonalLoanWorksheetDto = yup.InferType<ReturnType<typeof buildEditPersonalLoanWorksheetDtoSchema>>

export const EditWorksheetPaymentSplitDtoSchema = yup.object({
  merchantPayments: yup.mixed<MerchantPayment[]>().default([]),
  creditApplicationId: yup.string(),
})
export type EditWorksheetPaymentSplitDto = yup.InferType<typeof EditWorksheetPaymentSplitDtoSchema>
